import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ReportDesignCanvas from "@/components/report/ReportDesignCanvas";
import ReportPrintPreview from "@/components/report/ReportPrintPreview";
import { GRD } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { canWriteMainTab } from "@/lib/maintabAccess";
import {
  getGenericReportTemplate,
  saveGenericReportTemplate,
} from "@/lib/genericReportTemplateApi";
import "@/styles/emr-report-designer.css";

function emptyDesign() {
  return {
    firstPageHeaderHtml: "",
    subsequentHeaderHtml: "",
    footerHtml: "",
  };
}

function apiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return fallback;
}

export default function GenericReportDesignerPage() {
  const { user, isAdmin } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "generic-report-designer", { isAdmin });
  const [design, setDesign] = useState(emptyDesign);
  const [saved, setSaved] = useState(emptyDesign);
  const [meta, setMeta] = useState({ versionId: 0, isDefault: true, updatedAt: null });
  const [mode, setMode] = useState("design");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getGenericReportTemplate();
      const next = {
        firstPageHeaderHtml: data.firstPageHeaderHtml || "",
        subsequentHeaderHtml: data.subsequentHeaderHtml || "",
        footerHtml: data.footerHtml || "",
      };
      setDesign(next);
      setSaved(next);
      setMeta({
        versionId: data.versionId ?? 0,
        isDefault: Boolean(data.isDefault),
        updatedAt: data.updatedAt || null,
      });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to load Generic Report Template."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty =
    design.firstPageHeaderHtml !== saved.firstPageHeaderHtml ||
    design.subsequentHeaderHtml !== saved.subsequentHeaderHtml ||
    design.footerHtml !== saved.footerHtml;

  const onChange = (field, html) => {
    setDesign((current) => ({ ...current, [field]: html }));
  };

  const onSave = async () => {
    if (!canWrite) return;
    setSaving(true);
    try {
      const data = await saveGenericReportTemplate(design);
      const next = {
        firstPageHeaderHtml: data.firstPageHeaderHtml || "",
        subsequentHeaderHtml: data.subsequentHeaderHtml || "",
        footerHtml: data.footerHtml || "",
      };
      setDesign(next);
      setSaved(next);
      setMeta({
        versionId: data.versionId ?? 0,
        isDefault: Boolean(data.isDefault),
        updatedAt: data.updatedAt || null,
      });
      toast.success("Generic Report Template saved.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save Generic Report Template."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={GRD.page} className="grd">
      <div className="grd__pagehead">
        <div>
          <h1 data-testid={GRD.title} className="grd__title">
            Generic Report Designer
          </h1>
          <p className="grd__subtitle">
            Experimental printable report shell. One template applies to Office, Work,
            Leave, Operative, and other generated notes. Body content is not designed here.
          </p>
        </div>
        <div className="grd__actions">
          <div className="grd__modes">
            <button
              type="button"
              data-testid={GRD.designTab}
              className={`grd__mode ${mode === "design" ? "is-active" : ""}`}
              onClick={() => setMode("design")}
            >
              Design
            </button>
            <button
              type="button"
              data-testid={GRD.previewTab}
              className={`grd__mode ${mode === "preview" ? "is-active" : ""}`}
              onClick={() => setMode("preview")}
            >
              Preview
            </button>
          </div>
          <Button
            type="button"
            data-testid={GRD.saveBtn}
            disabled={!canWrite || saving || loading || !dirty}
            onClick={onSave}
            className="h-8 bg-teal-700 px-3 text-xs hover:bg-teal-800"
          >
            <Save className="h-3.5 w-3.5" />
            Save
          </Button>
        </div>
      </div>

      <p className="grd__banner">
        Experimental prototype. Saving updates the single active Generic Report Template.
        Placeholders resolve with sample data in Preview only.
      </p>

      <p data-testid={GRD.version} className="grd__meta" style={{ marginBottom: 12 }}>
        {loading
          ? "Loading template…"
          : meta.isDefault
            ? "Unsaved starter layout (version 0)"
            : `Version ${meta.versionId}${dirty ? " · unsaved changes" : ""}`}
      </p>

      {mode === "design" ? (
        <ReportDesignCanvas
          firstPageHeaderHtml={design.firstPageHeaderHtml}
          subsequentHeaderHtml={design.subsequentHeaderHtml}
          footerHtml={design.footerHtml}
          onChange={onChange}
          disabled={!canWrite || loading}
        />
      ) : (
        <ReportPrintPreview
          firstPageHeaderHtml={design.firstPageHeaderHtml}
          subsequentHeaderHtml={design.subsequentHeaderHtml}
          footerHtml={design.footerHtml}
        />
      )}

      <p className="grd__meta" style={{ marginTop: 12 }}>
        <Link to="/settings" className="text-teal-700 hover:underline">
          Back to Settings
        </Link>
        {" · "}
        <Link to="/placeholder-vocabulary" className="text-teal-700 hover:underline">
          Placeholder Vocabulary
        </Link>
      </p>
      <p data-testid={GRD.status} className="sr-only">
        {dirty ? "dirty" : "saved"}
      </p>
    </div>
  );
}
